import React from "react";
import { Button, FormHelperText, Stack, Tooltip } from "@mui/material";
import { useAuth } from "src/components/providers/auth-context";
import styled from "styled-components";
import request from "src/utils/request";
import { useTeam } from "./team-provider";
import useTeamAction from "../hooks/use-team-action";

const HtmlTooltip = styled(Tooltip)`
    cursor: pointer;
`;

const MakeOwnerButton = ({ setResponse, onSuccess, team, member }) => {
    const makeOwnerAction = useTeamAction(
        async () => {
            await request(`/api/teams/${team.id}`, {
                method: "POST",
                body: JSON.stringify({ ownerId: member.id }),
            });
            onSuccess();
            setResponse({ message: `Team owner successfully updated` });
        },
        () => setResponse({ message: `You can not perform this action on demo mode` })
    );

    const handleClick = async () => {
        setResponse({ message: undefined, error: undefined });
        try {
            makeOwnerAction();
        } catch (e) {
            setResponse({ message: `There was an error making this user an owner`, error: true });
        }
    };

    return (
        <Button variant="contained" style={{ textTransform: "none" }} onClick={handleClick}>
            Make Owner
        </Button>
    );
};

// eslint-disable-next-line no-unused-vars
const RequestOwnershipButton = ({ setResponse, onSuccess, team, user }) => {
    const requestOwnershipAction = useTeamAction(
        async () => {
            await request(`/api/teams/${team.id}/request`, {
                method: "PUT",
                body: JSON.stringify({ owner: user.id }),
            });
            onSuccess();
            setResponse({ message: `Successfully requested ownership transfer` });
        },
        () => setResponse({ message: `You can not perform this action on demo mode` })
    );

    const handleClick = async () => {
        setResponse({ message: undefined, error: undefined });
        try {
            requestOwnershipAction();
        } catch (e) {
            setResponse({
                message: `There was an error requesting ownership transfer `,
                error: true,
            });
        }
    };

    return (
        <Button variant="contained" style={{ textTransform: "none" }} onClick={handleClick}>
            Request Ownership
        </Button>
    );
};

const RemoveButton = ({ disabled, setResponse, onSuccess, team, member }) => {
    const removeAction = useTeamAction(
        async () => {
            await request(`/api/teams/${team.id}/members`, {
                method: "DELETE",
                body: JSON.stringify([member.id]),
            });
            onSuccess();
            setResponse({ message: `Successfully removed user from team` });
        },
        () => setResponse({ message: `You can not perform this action on demo mode` })
    );

    const removeUnregisteredAction = useTeamAction(
        async () => {
            await request(`/api/teams/${team.id}/temp-members`, {
                method: "DELETE",
                body: JSON.stringify([member.email]),
            });
            onSuccess();
            setResponse({ message: `Successfully removed user from team` });
        },
        () => setResponse({ message: `You can not perform this action on demo mode` })
    );

    const handleClick = async () => {
        setResponse({ message: undefined, error: undefined });
        try {
            if (member.id) {
                await removeAction();
            } else {
                await removeUnregisteredAction();
            }
        } catch (e) {
            setResponse({ message: "There was an error removing user from team", error: true });
        }
    };

    if (!disabled)
        return (
            <Button
                color="error"
                style={{ textTransform: "none" }}
                disabled={disabled}
                onClick={handleClick}
            >
                Remove
            </Button>
        );
    return (
        <HtmlTooltip title="Removing the team owner is not allowed. Ask the owner to transfer the ownership of the team to you first">
            <div style={{ display: "flex", justifyContent: "inherit", alignItems: "inherit" }}>
                <Button color="error" style={{ textTransform: "none" }} disabled={disabled}>
                    Remove
                </Button>
            </div>
        </HtmlTooltip>
    );
};

const LeaveButton = ({ disabled, setResponse, onSuccess, team }) => {
    const leaveAction = useTeamAction(
        async () => {
            await request(`/api/teams/${team.id}/leave`, { method: "DELETE" });
            setResponse({ message: `You were successfully removed from the team` });
            onSuccess();
        },
        () => setResponse({ message: `You can not perform this action on demo mode` })
    );

    const handleClick = async () => {
        setResponse({ message: undefined, error: undefined });
        try {
            leaveAction();
        } catch (e) {
            setResponse({ message: `There was an error removing you from team`, error: true });
        }
    };

    if (!disabled)
        return (
            <Button
                color="error"
                style={{ textTransform: "none" }}
                disabled={disabled}
                onClick={handleClick}
            >
                Leave Team
            </Button>
        );
    return (
        <HtmlTooltip
            title={`The team owner can't leave team. Transfer ownership to be able to leave the team`}
        >
            <div style={{ display: "flex", justifyContent: "inherit", alignItems: "inherit" }}>
                <Button color="error" style={{ textTransform: "none" }} disabled={disabled}>
                    Leave Team
                </Button>
            </div>
        </HtmlTooltip>
    );
};

const ResponseText = styled(FormHelperText)`
    color: ${({ theme, error }) => (error ? theme.palette.error.main : theme.palette.primary.main)};
`;

const TeamMemberCardActions = ({ member, onModalClose, isUnregistered }) => {
    const [response, setResponse] = React.useState({ message: undefined, error: undefined });
    const { user } = useAuth();
    const teamApi = useTeam();

    if (!teamApi || !teamApi.data) return null;

    const currentUserIsOwner = teamApi.data.owner.id === user.id;
    const memberIsUser = member.id === user.id;
    const memberIsTemp = !member.id;
    const memberIsOwner = member.id === teamApi.data.owner.id;

    const onSuccess = () => {
        teamApi.refetch();
        onModalClose();
    };

    const buttonProps = {
        setResponse,
        onSuccess,
        team: teamApi.data,
        member,
        user,
    };

    const renderButtons = () => {
        if (memberIsUser)
            return (
                <>
                    <div />
                    <LeaveButton disabled={currentUserIsOwner} {...buttonProps} />
                </>
            );
        if (memberIsOwner) {
            if (!currentUserIsOwner)
                return (
                    <>
                        <div />
                        <RemoveButton disabled isUnregistered={isUnregistered} {...buttonProps} />
                    </>
                );
        }
        if (currentUserIsOwner)
            return (
                <>
                    {!memberIsTemp && <MakeOwnerButton {...buttonProps} />}
                    <RemoveButton {...buttonProps} />
                </>
            );
        if (
            !memberIsUser &&
            (!memberIsOwner || (memberIsOwner && currentUserIsOwner)) &&
            !currentUserIsOwner
        )
            return (
                <>
                    <div />
                    <RemoveButton {...buttonProps} />
                </>
            );
        return null;
    };

    return (
        <>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
                {renderButtons()}
            </Stack>
            {response.message && (
                <ResponseText error={response.error} color="primary">
                    {response.message}
                </ResponseText>
            )}
        </>
    );
};

export default TeamMemberCardActions;
